import {
  createContext,
  useContext,
  useState,
  useCallback,
  useRef,
  useEffect,
  ReactNode,
} from 'react'

import { useAuth0 } from '@/auth/index' // Asumiendo que esta ruta es correcta
import { Context } from '@/shared/store/reducers/global-reducer' // Asumiendo que esta ruta es correcta

export interface FullAwsCredentialsResponse {
  credentials: AwsCredentials | null
  refreshToken: string | null
  applicationId: string | null
  authorized: boolean
  enableBidLibrary: boolean
}

interface AwsCredentials {
  AccessKeyId: string
  SecretAccessKey: string
  SessionToken: string
  Expiration: string
}

interface AwsCredentialsContextState {
  credentials: AwsCredentials | null
  isLoading: boolean
  error: Error | null
  getValidCredentials: () => Promise<AwsCredentials | null>
  hasAuth0TokenBeenSuccessfullyUsed: boolean // Renombrado y con nueva lógica
  applicationId: string | null
  isAuthorized: boolean | null
  enableBidLibrary: boolean | null
}

const AwsCredentialsContext = createContext<AwsCredentialsContextState | undefined>(undefined)

const EXPIRATION_BUFFER_MS = 5 * 60 * 1000

import { getConfig } from '@/config/runtime'

interface AwsCredentialsProviderProps {
  children: ReactNode
}

export const AwsCredentialsProvider = ({ children }: AwsCredentialsProviderProps) => {
  const { user, getIdTokenClaims, isAuthenticated } = useAuth0()
  const { store } = useContext(Context)
  const { activeTenant } = store

  const [awsCredentials, setAwsCredentials] = useState<AwsCredentials | null>(null)
  const [refreshToken, setRefreshToken] = useState<string | null>(null)
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [error, setError] = useState<Error | null>(null)
  const [hasAuth0TokenBeenSuccessfullyUsed, setHasAuth0TokenBeenSuccessfullyUsed] =
    useState<boolean>(false)
  const [applicationId, setApplicationId] = useState<string | null>(null)
  const [isAuthorized, setIsAuthorized] = useState<boolean | null>(null)
  const [enableBidLibrary, setEnableBidLibrary] = useState<boolean | null>(null)

  const isFetchingRef = useRef(false)

  const areCredentialsStillValid = useCallback((credsToCheck: AwsCredentials | null): boolean => {
    if (!credsToCheck?.Expiration) return false
    try {
      const expirationDate = new Date(credsToCheck.Expiration).getTime()
      const nowWithBuffer = Date.now() + EXPIRATION_BUFFER_MS
      return expirationDate > nowWithBuffer
    } catch (e) {
      console.error('⚠️ Error parsing expiration date:', e)
      return false
    }
  }, [])

  const performFetchCredentials = useCallback(
    async (
      currentRefreshTokenParam?: string | null
    ): Promise<FullAwsCredentialsResponse | null> => {
      if (isFetchingRef.current) {
        console.warn('⏳ Credentials fetch already in progress')
        return null
      }

      if (!activeTenant?.id || !user?.email) {
        console.warn('❌ User or activeTenant not available for credentials fetch.')
        setError(new Error('User or activeTenant not available.'))
        return null
      }

      isFetchingRef.current = true
      setIsLoading(true)
      setError(null)

      const shouldUseAuth0Token = !currentRefreshTokenParam && !hasAuth0TokenBeenSuccessfullyUsed
      const identity = { tenant: activeTenant.id, email: user.email }

      try {
        const headers: HeadersInit = { 'Content-Type': 'application/json' }
        let body: string

        if (shouldUseAuth0Token) {
          console.log('🔐 Requesting credentials with Auth0 token')
          if (!isAuthenticated) {
            throw new Error('User not authenticated for Auth0 token request.')
          }
          const claims = await getIdTokenClaims()
          if (!claims?.__raw) {
            throw new Error('Could not obtain Auth0 token.')
          }
          headers['Authorization'] = `Bearer ${claims.__raw}`
          body = JSON.stringify(identity)
        } else if (currentRefreshTokenParam) {
          console.log('🔄 Refreshing credentials with existing refresh token')
          body = JSON.stringify({ refreshToken: currentRefreshTokenParam, ...identity })
        } else {
          // Este caso no debería ocurrir si la lógica de llamada es correcta (o Auth0 o RT)
          throw new Error(
            'Invalid state: trying to fetch credentials without Auth0 or refresh token.'
          )
        }

        const CREDENTIALS_ENDPOINT = `${getConfig().plus.apiUrl}/get-credentials`
        const response = await fetch(CREDENTIALS_ENDPOINT, { method: 'POST', headers, body })
        const responseData = (await response.json()) as FullAwsCredentialsResponse

        if (!response.ok) {
          let errorMessage = `Network error (${response.status})`
          try {
            const errorBodyText = responseData?.toString() || (await response.text())
            errorMessage += `: ${errorBodyText}`
          } catch (e) {
            /* no action */
          }

          if (response.status === 401) {
            errorMessage += currentRefreshTokenParam
              ? ' (Invalid refreshToken)'
              : ' (Invalid Auth0 token)'
            if (currentRefreshTokenParam) {
              console.warn('🚫 Invalidating current refresh token due to 401.')
              setRefreshToken(null) // Invalidar el refresh token usado
            }
          }
          throw new Error(errorMessage)
        }

        const {
          credentials: receivedCredentials,
          refreshToken: newRefreshToken,
          applicationId: receivedApplicationId,
          authorized: receivedAuthorized,
          enableBidLibrary: receivedEnableBidLibrary,
        } = responseData

        if (typeof receivedAuthorized !== 'boolean') {
          throw new Error('API response missing or invalid "authorized" field.')
        }

        setIsAuthorized(receivedAuthorized)
        setEnableBidLibrary(receivedEnableBidLibrary ?? null)

        if (!receivedAuthorized) {
          console.log(`🚫 Tenant ${activeTenant.id} not authorized.`)
          setAwsCredentials(null)
          setApplicationId(null)
          // No limpiar refreshToken aquí si se usó uno; podría ser válido para otro tenant o un reintento.
          // No marcar hasAuth0TokenBeenSuccessfullyUsed como true.
          return responseData // { authorized: false, ... }
        }

        // Si authorized es true, todos los campos son esperados
        if (
          !receivedCredentials?.AccessKeyId ||
          !receivedCredentials.SecretAccessKey ||
          !receivedCredentials.SessionToken ||
          !receivedCredentials.Expiration ||
          typeof newRefreshToken !== 'string' ||
          newRefreshToken.trim() === '' ||
          typeof receivedApplicationId !== 'string' ||
          receivedApplicationId.trim() === ''
        ) {
          throw new Error('Authorized tenant response is incomplete or missing required fields.')
        }

        console.log(
          `✅ Credentials ${
            shouldUseAuth0Token
              ? 'obtained via Auth0 token'
              : currentRefreshTokenParam
              ? 'refreshed'
              : 'obtained'
          }`
        )
        setAwsCredentials(receivedCredentials)
        setRefreshToken(newRefreshToken) // Siempre actualizar con el nuevo token si la petición fue exitosa y autorizada
        setApplicationId(receivedApplicationId)

        if (shouldUseAuth0Token) {
          console.log('🔑 Auth0 token successfully used for the first time.')
          setHasAuth0TokenBeenSuccessfullyUsed(true)
        }
        return responseData
      } catch (err) {
        const typedError = err instanceof Error ? err : new Error(String(err))
        console.error(
          `❌ Error ${
            shouldUseAuth0Token
              ? 'obtaining with Auth0'
              : currentRefreshTokenParam
              ? 'refreshing'
              : 'obtaining'
          } credentials:`,
          typedError.message
        )
        setError(typedError)
        setAwsCredentials(null)
        setApplicationId(null)
        setIsAuthorized(false) // En caso de error, asumir no autorizado o estado desconocido/fallido
        setEnableBidLibrary(null)
        // No modificar hasAuth0TokenBeenSuccessfullyUsed en error.
        // Si el refreshToken se invalidó (401), ya fue limpiado.
        return null
      } finally {
        isFetchingRef.current = false
        setIsLoading(false)
      }
    },
    [getIdTokenClaims, isAuthenticated, user, activeTenant, hasAuth0TokenBeenSuccessfullyUsed]
  )

  const getValidCredentials = useCallback(async (): Promise<AwsCredentials | null> => {
    if (isAuthorized && areCredentialsStillValid(awsCredentials)) {
      console.log('✅ Using valid credentials from memory')
      return awsCredentials
    }

    // Si hay un refreshToken, siempre intentar usarlo primero si el token de Auth0 ya fue usado o si el RT existe.
    // La lógica de `performFetchCredentials` decidirá si realmente usa el RT o Auth0 basado en `hasAuth0TokenBeenSuccessfullyUsed`
    // si `refreshToken` es null.
    if (refreshToken) {
      console.log('🔄 Attempting fetch/refresh with existing refresh token.')
      const refreshResponse = await performFetchCredentials(refreshToken)
      if (refreshResponse?.authorized && refreshResponse.credentials) {
        return refreshResponse.credentials
      }
      // Si authorized es false o hay error, performFetchCredentials ya manejó los estados.
      // Si el refreshToken se invalidó (401), el estado `refreshToken` ahora será null.
    }

    // Si no hay refreshToken (o se invalidó) O si el intento anterior con RT no dio credenciales,
    // y el token de Auth0 aún no se ha usado con éxito:
    if (!hasAuth0TokenBeenSuccessfullyUsed) {
      console.log('🔐 Requesting new credentials using Auth0 token logic.')
      const initialFetchResponse = await performFetchCredentials(null)
      if (initialFetchResponse?.authorized && initialFetchResponse.credentials) {
        return initialFetchResponse.credentials
      }
      return null
    }

    // Si llegamos aquí:
    // 1. No hay credenciales válidas en memoria.
    // 2. O no había refreshToken, o el intento con refreshToken falló (no autorizado, o error).
    // 3. El token de Auth0 ya fue usado exitosamente.
    if (hasAuth0TokenBeenSuccessfullyUsed && !refreshToken && !awsCredentials) {
      console.warn(
        '⚠️ Cannot fetch credentials: Auth0 token already used, and no refreshToken available.'
      )
    } else if (error && !awsCredentials) {
      console.error('❌ Could not obtain credentials due to a previous error:', error.message)
    }

    return null
  }, [
    awsCredentials,
    refreshToken,
    areCredentialsStillValid,
    performFetchCredentials,
    hasAuth0TokenBeenSuccessfullyUsed,
    isAuthorized,
    error,
  ])

  // Effect for initial credentials fetch or when auth state changes
  useEffect(() => {
    if (
      isAuthenticated &&
      user &&
      activeTenant &&
      !awsCredentials && // No tenemos credenciales válidas
      !isLoading &&
      !isFetchingRef.current &&
      !hasAuth0TokenBeenSuccessfullyUsed && // El token de Auth0 no se ha usado con éxito
      isAuthorized === null // Solo si no se ha determinado el estado de autorización (evita re-fetchs si ya es false)
    ) {
      console.log('🚀 Dispatching initial credentials fetch (Auth0 logic will apply if needed).')
      getValidCredentials()
    }
  }, [
    isAuthenticated,
    user,
    activeTenant,
    awsCredentials,
    isLoading,
    hasAuth0TokenBeenSuccessfullyUsed,
    isAuthorized,
    getValidCredentials,
  ])

  const prevActiveTenantRef = useRef(activeTenant)

  // Effect for tenant switching
  useEffect(() => {
    const prevTenant = prevActiveTenantRef.current
    const currentTenant = activeTenant

    if (currentTenant?.id && prevTenant?.id !== currentTenant?.id && isAuthenticated) {
      console.log(
        `🔄 Tenant changed from ${prevTenant?.id || 'none'} to ${
          currentTenant.id
        }. Refetching credentials.`
      )

      setAwsCredentials(null)
      setApplicationId(null)
      setEnableBidLibrary(null)
      // No limpiar refreshToken aquí; se conservará si la nueva petición falla sin invalidarlo.
      setIsAuthorized(null) // Reset authorization state for the new tenant.
      setError(null) // Clear previous errors.

      // Decidir cómo obtener credenciales para el nuevo tenant:
      // 1. Si hay un refreshToken y el token de Auth0 ya se usó exitosamente, intentar con el refreshToken.
      // 2. Si el token de Auth0 aún no se ha usado exitosamente, intentar con Auth0.
      // 3. Si el token de Auth0 ya se usó y no hay refreshToken (o se invalidó), no se puede hacer nada.
      if (refreshToken && hasAuth0TokenBeenSuccessfullyUsed) {
        console.log('Tenant changed: Attempting fetch with existing refreshToken.')
        performFetchCredentials(refreshToken)
      } else if (!hasAuth0TokenBeenSuccessfullyUsed) {
        console.log('Tenant changed: Attempting fetch with Auth0 token.')
        performFetchCredentials(null)
      } else {
        console.warn(
          'Tenant changed: Auth0 token already used, and no refreshToken available. Cannot fetch credentials for this tenant.'
        )
        setIsAuthorized(false)
        setError(
          new Error(
            'Cannot fetch credentials: Auth0 token used and no refresh token for new tenant.'
          )
        )
      }
    }
    prevActiveTenantRef.current = currentTenant
  }, [
    activeTenant,
    isAuthenticated,
    performFetchCredentials,
    refreshToken,
    hasAuth0TokenBeenSuccessfullyUsed,
  ])

  return (
    <AwsCredentialsContext.Provider
      value={{
        credentials: awsCredentials,
        isLoading,
        error,
        getValidCredentials,
        hasAuth0TokenBeenSuccessfullyUsed,
        applicationId,
        isAuthorized,
        enableBidLibrary,
      }}
    >
      {children}
    </AwsCredentialsContext.Provider>
  )
}

export const useAwsCredentialsManager = (): AwsCredentialsContextState => {
  const context = useContext(AwsCredentialsContext)
  if (context === undefined) {
    throw new Error('useAwsCredentialsManager must be used within an AwsCredentialsProvider')
  }
  return context
}
